import get from "lodash.get";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { getLocalStorageItem } from "../../../utils/storage/localStorageUtil";
import Typography from "../../../components/atoms/typography";
import { getRecentViewedByYouAPI } from "../../../api/pdpApi";
import Widget from "../../../components/organisms/widget";
import { PageTypes } from "../../../utils/fnpPageType";
import useIntersectionObserver from "../../../customHooks/intersectionObserver";

/**
 * HomeWidgetSection component renders a product widget when it enters the viewport.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.isMobile - Indicates if the view is mobile
 * @param {Object} props.data - Data object containing product information
 * @param {string} props.data.category_Name - Category name of the products
 * @param {Object[]} props.data.products - List of products in the category
 * @param {string} [props.headingClass] - Additional CSS classes for the heading
 * @returns {JSX.Element} The HomeWidgetSection component
 */
const HomeWidgetSection = ({ isMobile, data, headingClass }) => {
  const [products, setProducts] = useState([]);
  const widgetRef = useRef(null);

  // Detect if the widget is in the viewport
  const loadWidgetLazy = useIntersectionObserver(widgetRef, { freezeOnceVisible: true });

  useEffect(() => {
    if (loadWidgetLazy) {
      /**
       * fetch widget data
       */
      const fetchRecentlyViewedByYou = async () => {
        const recentlyViewedProducts = JSON.parse(getLocalStorageItem("recentlyViewedProducts") || "[]");
        const response = await getRecentViewedByYouAPI(data?.products || recentlyViewedProducts);
        setProducts(response?.data);
      };

      fetchRecentlyViewedByYou();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadWidgetLazy, data?.category_Name]);

  return (
    <>
      {!loadWidgetLazy || products?.productResults?.length > 0 ? (
        <div ref={widgetRef} className="relative w-full md:px-40">
          {data?.category_Name && (
            <Typography
              variant="h1"
              Tag="p"
              classes={`font-600 text-16 mb-16 md:mb-0 px-16 md:px-0 lg:text-24 xxl:text-32 ${headingClass || ""} capitalize`}
            >
              {data?.category_Name}
            </Typography>
          )}
          {loadWidgetLazy && (
            <div className="flex w-full px-0">
              <Widget
                productsList={get(products, "productResults", [])}
                isMobile={isMobile}
                pageType={PageTypes?.HOME}
                isHideSavedPercent={true}
                promo={data?.trackingParam}
                viewAll={data?.viewAll}
                widgetTitle={data?.category_Name}
                isShowTitle={false}
              />
            </div>
          )}
        </div>
      ) : (
        ""
      )}
    </>
  );
};
HomeWidgetSection.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  data: PropTypes.object.isRequired,
  headingClass: PropTypes.string,
};
export default HomeWidgetSection;
